import { supabase } from '../auth/supabaseClient'
import { telemetry } from './telemetry'

type SetSummary = { reps: number; durationMs: number }
type SessionSummary = { reps: number; sets: SetSummary[] }

const KEY = 'pt.sync.queue.v1'

type QueueItem = { id: string; payload: SessionSummary }
type SyncListener = (evt: { type: 'drain-success'; count: number } | { type: 'drain-error' }) => void
const listeners: SyncListener[] = []

function load(): QueueItem[] { try { return JSON.parse(localStorage.getItem(KEY) || '[]') } catch { return [] } }
function save(q: QueueItem[]) { try { localStorage.setItem(KEY, JSON.stringify(q)) } catch { /* ignore */ } }

export function enqueue(summary: SessionSummary) {
  const q = load(); q.push({ id: Math.random().toString(36).slice(2), payload: summary }); save(q)
}

export async function drain() {
  let q = load()
  if (!q.length) return
  let drained = 0
  try {
    // Skip remote writes when not authenticated; keep queue for later
    const { data: auth } = await supabase.auth.getSession()
    if (!auth.session) return
    const uid = auth.session.user.id
    while (q.length) {
      const next = q[0]
      const startedAt = new Date().toISOString()
      const endedAt = new Date().toISOString()
      const { data: sess, error } = await supabase.from('sessions').insert({
        user_id: uid,
        started_at: startedAt,
        ended_at: endedAt,
        total_reps: next.payload.reps,
        best_set_reps: Math.max(0, ...next.payload.sets.map(s=>s.reps)),
        app_version: (import.meta as any).env?.VITE_APP_VERSION ?? undefined
      }).select('id').single()
      if (error) throw error
      if (sess?.id && next.payload.sets.length) {
        const rows = next.payload.sets.map((s, i) => ({ session_id: sess.id, idx: i+1, reps: s.reps, duration_s: Math.round(s.durationMs/1000) }))
        const { error: err2 } = await supabase.from('session_sets').insert(rows)
        if (err2) throw err2
      }
      q = load() // reload in case of concurrent updates
      q.shift(); save(q)
      drained++
    }
    if (drained > 0) {
      telemetry.log('sync_drain_success', { count: drained })
      listeners.forEach(l => l({ type: 'drain-success', count: drained }))
    }
  } catch (_e) {
    telemetry.log('sync_drain_error')
    listeners.forEach(l => l({ type: 'drain-error' }))
  }
}

export function setupSync() {
  window.addEventListener('online', () => { drain() })
}

export function onSync(listener: SyncListener) {
  listeners.push(listener)
  return () => {
    const i = listeners.indexOf(listener)
    if (i >= 0) listeners.splice(i, 1)
  }
}
